const API_URL = import.meta.env.VITE_API_URL || 'https://fiifit.ro';

export interface NutritionInfo {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber?: number;
  sugar?: number;
  sodium?: number;
}

export interface FoodItem {
  id?: string;
  name: string;
  brand?: string;
  barcode?: string;
  servingSize: number;
  servingUnit: string;
  nutrition: NutritionInfo;
  imageUrl?: string;
}

export interface FoodEntry {
  id: string;
  userId: string;
  foodName: string;
  brand?: string;
  barcode?: string;
  servingSize: number;
  servingUnit: string;
  quantity: number;
  nutrition: NutritionInfo;
  mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  date: string;
  consumedAt?: string;
  notes?: string;
  imageUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CalorieGoals {
  id: string;
  userId: string;
  targetCalories: number;
  targetProtein: number;
  targetCarbs: number;
  targetFats: number;
  activityLevel: string;
  goalType: string;
  bmr?: number;
  tdee?: number;
  createdAt: string;
  updatedAt: string;
}

export interface FoodHistoryItem {
  foodName: string;
  brand: string | null;
  barcode: string | null;
  servingSize: number;
  servingUnit: string;
  calories: number;
  protein: number;
  carbs: number;
  fats: number;
  lastUsed: string;
  timesUsed: number;
  lastMealType: string;
  imageUrl: string | null;
}

export interface DailyNutrition {
  date: string;
  entries: FoodEntry[];
  totals: NutritionInfo & { quantity: number };
  goals?: CalorieGoals;
  remaining: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
  };
  byMealType: {
    breakfast: NutritionInfo;
    lunch: NutritionInfo;
    dinner: NutritionInfo;
    snack: NutritionInfo;
  };
}

class FoodTrackingService {
  private getAuthHeader() {
    const token = localStorage.getItem('accessToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  private getAuthOnlyHeader() {
    const token = localStorage.getItem('accessToken');
    return {
      'Authorization': `Bearer ${token}`,
    };
  }

  // Search foods
  async searchFood(query: string, page = 1, limit = 20): Promise<{ foods: FoodItem[], total: number }> {
    const response = await fetch(
      `${API_URL}/api/food-tracking/search?q=${encodeURIComponent(query)}&page=${page}&limit=${limit}`,
      {
        headers: this.getAuthOnlyHeader(),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to search food');
    }

    return response.json();
  }

  // Search by barcode
  async searchByBarcode(barcode: string): Promise<FoodItem | null> {
    const response = await fetch(
      `${API_URL}/api/food-tracking/barcode/${barcode}`,
      {
        headers: this.getAuthOnlyHeader(),
      }
    );

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error('Failed to search by barcode');
    }

    return response.json();
  }

  // AI nutrition completion
  async getNutritionFromAI(foodName: string, brand?: string): Promise<any> {
    const response = await fetch(`${API_URL}/api/food-tracking/food/ai-complete`, {
      method: 'POST',
      headers: this.getAuthHeader(),
      body: JSON.stringify({ foodName, brand }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || 'Failed to get nutrition from AI');
    }

    return response.json();
  }

  // Create custom food
  async createCustomFood(food: Omit<FoodItem, 'id'>): Promise<FoodItem> {
    const response = await fetch(`${API_URL}/api/food-tracking/food/custom`, {
      method: 'POST',
      headers: this.getAuthHeader(),
      body: JSON.stringify(food),
    });

    if (!response.ok) {
      throw new Error('Failed to create custom food');
    }

    return response.json();
  }

  // Goals
  async getGoals(): Promise<CalorieGoals> {
    const response = await fetch(`${API_URL}/api/food-tracking/goals`, {
      headers: this.getAuthOnlyHeader(),
    });

    if (!response.ok) {
      throw new Error('Failed to get goals');
    }

    return response.json();
  }

  async calculateGoals(data: {
    age: number;
    gender: 'male' | 'female';
    weight: number;
    height: number;
    activityLevel: 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active';
    goal: 'lose' | 'maintain' | 'gain' | 'lose_slow' | 'gain_healthy';
  }): Promise<CalorieGoals> {
    // Map frontend goal values to backend goalType values
    const goalTypeMap = {
      'lose': 'weight_loss',
      'lose_slow': 'weight_loss_slow',
      'maintain': 'maintain',
      'gain': 'muscle_gain',
      'gain_healthy': 'healthy_weight_gain'
    } as const;

    const payload = {
      age: data.age,
      gender: data.gender,
      weight: data.weight,
      height: data.height,
      activityLevel: data.activityLevel,
      goalType: goalTypeMap[data.goal]
    };

    const response = await fetch(`${API_URL}/api/food-tracking/goals/calculate`, {
      method: 'POST',
      headers: this.getAuthHeader(),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('Backend error:', errorData);
      throw new Error(errorData.error || `Server error: ${response.status}`);
    }

    return response.json();
  }

  async updateGoals(goals: Partial<CalorieGoals>): Promise<CalorieGoals> {
    const response = await fetch(`${API_URL}/api/food-tracking/goals`, {
      method: 'PUT',
      headers: this.getAuthHeader(),
      body: JSON.stringify(goals),
    });

    if (!response.ok) {
      throw new Error('Failed to update goals');
    }

    return response.json();
  }

  // Food Entries
  async createEntry(entry: {
    foodName: string;
    brand?: string;
    barcode?: string;
    servingSize: number;
    servingUnit: string;
    quantity: number;
    nutrition: NutritionInfo;
    mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack';
    date: string;
    notes?: string;
    imageUrl?: string;
  }): Promise<FoodEntry> {
    const response = await fetch(`${API_URL}/api/food-tracking/entries`, {
      method: 'POST',
      headers: this.getAuthHeader(),
      body: JSON.stringify(entry),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to create entry' }));
      throw new Error(errorData.error || 'Failed to create entry');
    }

    return response.json();
  }

  async getEntries(params?: {
    startDate?: string;
    endDate?: string;
    mealType?: string;
    page?: number;
    limit?: number;
  }): Promise<{ entries: FoodEntry[], total: number }> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.mealType) queryParams.append('mealType', params.mealType);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const response = await fetch(
      `${API_URL}/api/food-tracking/entries?${queryParams}`,
      {
        headers: this.getAuthOnlyHeader(),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to get entries');
    }

    return response.json();
  }

  async getEntry(id: string): Promise<FoodEntry> {
    const response = await fetch(`${API_URL}/api/food-tracking/entries/${id}`, {
      headers: this.getAuthOnlyHeader(),
    });

    if (!response.ok) {
      throw new Error('Failed to get entry');
    }

    return response.json();
  }

  async updateEntry(id: string, data: Partial<FoodEntry>): Promise<FoodEntry> {
    const response = await fetch(`${API_URL}/api/food-tracking/entries/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeader(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to update entry');
    }

    return response.json();
  }

  async deleteEntry(id: string): Promise<void> {
    const response = await fetch(`${API_URL}/api/food-tracking/entries/${id}`, {
      method: 'DELETE',
      headers: this.getAuthOnlyHeader(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Eroare la ștergerea înregistrării' }));
      throw new Error(errorData.error || 'Eroare la ștergerea înregistrării');
    }
  }

  // Daily nutrition
  async getDailyNutrition(date: string): Promise<DailyNutrition> {
    const response = await fetch(
      `${API_URL}/api/food-tracking/daily/${date}`,
      {
        headers: this.getAuthOnlyHeader(),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to get daily nutrition');
    }

    return response.json();
  }

  // Weekly nutrition
  async getWeeklyNutrition(startDate: string): Promise<any> {
    const response = await fetch(
      `${API_URL}/api/food-tracking/week?startDate=${startDate}`,
      {
        headers: this.getAuthOnlyHeader(),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to get weekly nutrition');
    }

    return response.json();
  }

  // Monthly nutrition
  async getMonthlyNutrition(year: number, month: number): Promise<any> {
    const response = await fetch(
      `${API_URL}/api/food-tracking/month?year=${year}&month=${month}`,
      {
        headers: this.getAuthOnlyHeader(),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to get monthly nutrition');
    }

    return response.json();
  }

  // Statistics
  async getStats(period: 'week' | 'month' | 'year' = 'week'): Promise<any> {
    const response = await fetch(
      `${API_URL}/api/food-tracking/stats?period=${period}`,
      {
        headers: this.getAuthOnlyHeader(),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to get stats');
    }

    return response.json();
  }

  // Favorites
  async addFavorite(food: FoodItem): Promise<void> {
    const response = await fetch(`${API_URL}/api/food-tracking/favorites`, {
      method: 'POST',
      headers: this.getAuthHeader(),
      body: JSON.stringify(food),
    });

    if (!response.ok) {
      throw new Error('Failed to add favorite');
    }
  }

  async getFavorites(): Promise<FoodItem[]> {
    const response = await fetch(`${API_URL}/api/food-tracking/favorites`, {
      headers: this.getAuthOnlyHeader(),
    });

    if (!response.ok) {
      throw new Error('Failed to get favorites');
    }

    return response.json();
  }

  async removeFavorite(id: string): Promise<void> {
    const response = await fetch(`${API_URL}/api/food-tracking/favorites/${id}`, {
      method: 'DELETE',
      headers: this.getAuthOnlyHeader(),
    });

    if (!response.ok) {
      throw new Error('Failed to remove favorite');
    }
  }

  // Recent foods
  async getRecentFoods(limit = 10): Promise<FoodItem[]> {
    const response = await fetch(
      `${API_URL}/api/food-tracking/recent?limit=${limit}`,
      {
        headers: this.getAuthOnlyHeader(),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to get recent foods');
    }

    return response.json();
  }

  // Food history (deduplicated, sorted by frequency)
  async getFoodHistory(limit = 50): Promise<FoodHistoryItem[]> {
    const response = await fetch(
      `${API_URL}/api/food-tracking/history?limit=${limit}`,
      {
        headers: this.getAuthOnlyHeader(),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to get food history');
    }

    return response.json();
  }
}

export default new FoodTrackingService();
